<template>
  <div class="log-row" :class="{ expanded, 'has-columns': columnMode }">
    <!-- Collapsed line: click anywhere on the summary toggles the detail -->
    <div
      class="summary"
      role="button"
      tabindex="0"
      :aria-expanded="expanded"
      :style="{ gridTemplateColumns: gridTemplate }"
      @click="$emit('toggle')"
      @keydown.enter.prevent="$emit('toggle')"
      @keydown.space.prevent="$emit('toggle')"
    >
      <div class="line1">
        <span class="col-time">{{ shortTime(row.ts) }}</span>
        <button
          class="level-badge"
          :class="`lvl-${levelClass(row.level)}`"
          :title="`Filter by level: ${levelLabel(row.level)}`"
          :disabled="!row.level"
          @click.stop="$emit('filter', 'level', (row.level ?? '').toLowerCase())"
        >
          {{ levelLabel(row.level) }}
        </button>
        <button
          class="col-container"
          :style="{ color: containerColor(row.container) }"
          :title="`Filter by container: ${row.container}`"
          @click.stop="$emit('filter', 'container', row.container)"
        >
          {{ row.container }}
        </button>
      </div>

      <!-- Desktop pinned-attr COLUMNS (between container and message). Each cell
           is this row's value for the column key (parsed once), empty if absent. -->
      <template v-if="columnMode">
        <span v-for="p in pinValues" :key="p.key" class="pin-cell" :title="p.raw || undefined">
          <span class="pin-cell-val">{{ p.value }}</span>
          <!-- Copy affordance: absolutely positioned so it never widens the grid
               track or forces horizontal overflow; revealed on hover/focus. -->
          <button
            v-if="canCopy && p.raw"
            type="button"
            class="cell-copy"
            :class="{ copied: copiedToken === 'col:' + p.key }"
            :title="copiedToken === 'col:' + p.key ? 'Copied' : `Copy ${p.key}`"
            :aria-label="`Copy value of ${p.key}`"
            @click.stop="copyValue('col:' + p.key, p.raw)"
          >
            <Check v-if="copiedToken === 'col:' + p.key" :size="11" aria-hidden="true" />
            <Copy v-else :size="11" aria-hidden="true" />
          </button>
        </span>
      </template>

      <span class="col-message" :class="{ err: row.stream === 'stderr' }">{{ row.message }}</span>

      <!-- Labeled pin line: the compact `key=value` fallback. Shown on MOBILE
           always, and on DESKTOP only when NOT in column mode (>6 keys). CSS
           handles the desktop/mobile visibility; it spans the full grid width. -->
      <span v-if="pinLinePairs.length" class="pin-line">
        <span v-for="p in pinLinePairs" :key="p.key" class="pin-pair"
          ><span class="pin-k">{{ p.key }}</span
          >=<span class="pin-v">{{ p.value }}</span></span
        >
      </span>
    </div>

    <!-- Expanded detail -->
    <div v-if="expanded" class="detail">
      <div class="detail-meta">
        <span class="meta-item"><span class="meta-k">time</span>{{ isoTime(row.ts) }}</span>
        <span class="meta-item"><span class="meta-k">stream</span>{{ row.stream }}</span>
        <span v-if="row.service" class="meta-item"
          ><span class="meta-k">service</span>{{ row.service }}</span
        >
        <span v-if="row.stack" class="meta-item"
          ><span class="meta-k">stack</span>{{ row.stack }}</span
        >
      </div>
      <pre class="detail-message">{{ row.message }}</pre>
      <div v-if="attrsObj" class="detail-attrs">
        <div class="detail-attrs-head">
          <span class="detail-label">attrs</span>
          <button
            v-if="pinnedKeys && pinnedKeys.length"
            type="button"
            class="save-default-btn"
            :title="`Save the pins for ${row.container} as the org default (seeds fresh sessions)`"
            @click="$emit('save-default', row.container)"
          >
            Save as default
          </button>
        </div>
        <ul class="attrs-list">
          <li v-for="(v, k) in attrsObj" :key="k" class="attr-entry">
            <button
              type="button"
              class="attr-key"
              :title="`Filter by attr: ${String(k)}: ${displayValue(v)}`"
              @click="onAttrClick(String(k), v)"
            >
              {{ k }}
            </button>
            <span class="attr-eq" aria-hidden="true">=</span>
            <button
              type="button"
              class="attr-val"
              :title="`Filter by attr: ${String(k)}: ${displayValue(v)}`"
              @click="onAttrClick(String(k), v)"
            >
              {{ displayValue(v) }}
            </button>
            <button
              v-if="canCopy"
              type="button"
              class="copy-btn"
              :class="{ copied: copiedToken === 'attr:' + String(k) }"
              :title="copiedToken === 'attr:' + String(k) ? 'Copied' : 'Copy value'"
              :aria-label="`Copy value of ${String(k)}`"
              @click.stop="copyValue('attr:' + String(k), displayValue(v))"
            >
              <Check v-if="copiedToken === 'attr:' + String(k)" :size="12" aria-hidden="true" />
              <Copy v-else :size="12" aria-hidden="true" />
            </button>
            <LgTooltip
              :text="
                isPinned(String(k))
                  ? `Unpin ${String(k)} from this view`
                  : `Pin for this view (shows ${String(k)} as a column)`
              "
              placement="top"
            >
              <button
                type="button"
                class="pin-btn"
                :class="{ pinned: isPinned(String(k)) }"
                :aria-pressed="isPinned(String(k))"
                :aria-label="
                  isPinned(String(k))
                    ? `Unpin attribute ${String(k)} for this view`
                    : `Pin attribute ${String(k)} for this view`
                "
                @click.stop="$emit('toggle-pin', String(k))"
              >
                <Pin :size="12" aria-hidden="true" />
              </button>
            </LgTooltip>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeUnmount } from 'vue';
import { Pin, Copy, Check } from 'lucide-vue-next';
import type { LogRow } from '../api.js';
import { shortTime, isoTime, levelClass, levelLabel, containerColor } from '../format.js';
import { valueByteLen, MAX_TERM_VALUE_LEN } from '../composables/useFilters.js';
import LgTooltip from '../ui/LgTooltip.vue';

/** Column/line value cap — long attr values are ellipsized for display; the full
 * value stays in the `title` tooltip. */
const PIN_VALUE_MAX = 60;

const props = withDefaults(
  defineProps<{
    row: LogRow;
    expanded: boolean;
    /** Distinct pinned attr keys to surface (columns on desktop, labeled line on
     * mobile / when >6). Derived from session pins STATE, identical for every row. */
    columnKeys?: string[];
    /** True = desktop renders `columnKeys` as columns; false = labeled line only. */
    columnMode?: boolean;
    /** CSS `grid-template-columns` shared with the list header so they stay aligned. */
    gridTemplate?: string;
    /** Session-pinned keys for THIS row's container — drives the pin-button state. */
    pinnedKeys?: string[];
  }>(),
  { columnKeys: () => [], columnMode: false, gridTemplate: '', pinnedKeys: () => [] },
);

const emit = defineEmits<{
  toggle: [];
  filter: [kind: 'level' | 'container', value: string];
  /** Expanded-row attr key/value click — value is already clipped for the filter (<=256 chars). */
  'attr-filter': [key: string, value: string];
  /** Pin toggle now edits SESSION pins (useFilters) upstream, not the org setting. */
  'toggle-pin': [key: string];
  /** "Save as default" — persist this container's session pins to the org setting. */
  'save-default': [container: string];
}>();

// Parse this row's attrs ONCE (cached computed) — every column cell and the
// labeled line read from this single parse, never re-parsing per cell.
const attrsObj = computed<Record<string, unknown> | null>(() => {
  if (!props.row.attrs) return null;
  try {
    const parsed: unknown = JSON.parse(props.row.attrs);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
});

function displayValue(v: unknown): string {
  return typeof v === 'string' ? v : JSON.stringify(v);
}

function clip(v: string): string {
  return v.length > PIN_VALUE_MAX ? `${v.slice(0, PIN_VALUE_MAX)}…` : v;
}

function isPinned(key: string): boolean {
  return props.pinnedKeys.includes(key);
}

/**
 * Truncate to at most `maxBytes` UTF-8 bytes WITHOUT splitting a codepoint —
 * iterating codepoints (`for..of`) never lands mid-surrogate-pair. Used so the
 * value emitted to the filter is measured the same way the composable/server
 * cap it (BYTES, not UTF-16 length); clipping by `.length` could leave a
 * multibyte value over 256 bytes, which addAttrTerm then drops silently.
 */
function clipToBytes(v: string, maxBytes: number): string {
  if (valueByteLen(v) <= maxBytes) return v;
  let out = '';
  for (const ch of v) {
    if (valueByteLen(out + ch) > maxBytes) break;
    out += ch;
  }
  return out;
}

function onAttrClick(key: string, value: unknown) {
  emit('attr-filter', key, clipToBytes(displayValue(value), MAX_TERM_VALUE_LEN));
}

/** One entry per column key (in order): `raw` = full value (copied verbatim),
 * `value` = the ellipsized display string. */
const pinValues = computed(() => {
  // No pinned columns for this row -> bail before touching attrsObj, so a row
  // with nothing pinned never parses its attrs JSON on every 2s poll tick.
  if (!props.columnKeys.length) return [];
  const obj = attrsObj.value;
  return props.columnKeys.map((key) => {
    const raw = obj && key in obj ? displayValue(obj[key]) : '';
    return { key, raw, value: clip(raw) };
  });
});

/** The labeled-line pairs: only keys this row actually has a value for. */
const pinLinePairs = computed(() => pinValues.value.filter((p) => p.value !== ''));

// --- Copy-to-clipboard (attr values + column cells) ----------------------
// Guard: some contexts (insecure origin, older browsers) have no clipboard API.
// When it's absent we just don't render the copy affordances.
const canCopy = typeof navigator !== 'undefined' && !!navigator.clipboard;
/** Token of the value copied most recently, for transient "copied ✓" feedback.
 * Namespaced (`attr:<key>` / `col:<key>`) so the two surfaces never collide. */
const copiedToken = ref<string | null>(null);
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

async function copyValue(token: string, value: string) {
  if (!navigator.clipboard) return;
  try {
    await navigator.clipboard.writeText(value);
    copiedToken.value = token;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      copiedToken.value = null;
    }, 1500);
  } catch {
    /* clipboard write denied — leave the value in place, no feedback */
  }
}

onBeforeUnmount(() => clearTimeout(copiedTimer));
</script>

<style scoped>
.log-row {
  border-bottom: 1px solid var(--lg-border-faint);
}
.log-row.expanded {
  background: var(--lg-bg-raised);
}

.summary {
  display: grid;
  /* grid-template-columns is set inline (shared with the list header) so pinned
     columns stay aligned; this is the fallback when no template is provided. */
  grid-template-columns: 92px 58px minmax(90px, 180px) 1fr;
  gap: var(--lg-space-3);
  align-items: baseline;
  padding: var(--lg-space-1) var(--lg-space-3);
  cursor: pointer;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  line-height: var(--lg-lh-normal);
}
/* On desktop the line1 wrapper is transparent to the grid so its children
   land in the first three columns; pinned cells + the message follow. */
.line1 {
  display: contents;
}
.summary:hover {
  background: var(--lg-bg-hover);
}
.summary:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--lg-accent);
}

.col-time {
  color: var(--lg-text-dim);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.level-badge {
  font: inherit;
  font-size: var(--lg-fs-2xs);
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: center;
  padding: 0 var(--lg-space-1);
  border-radius: var(--lg-radius-sm);
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
}
.level-badge:disabled {
  cursor: default;
}
.level-badge:not(:disabled):hover {
  border-color: currentColor;
}
.lvl-error {
  color: var(--lg-level-error);
}
.lvl-warn {
  color: var(--lg-level-warn);
}
.lvl-info {
  color: var(--lg-level-info);
}
.lvl-debug {
  color: var(--lg-level-debug);
}
.lvl-null {
  color: var(--lg-level-null);
}

.col-container {
  font: inherit;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.col-container:hover {
  text-decoration: underline;
}

/* Pinned-attr column cell — dim, ellipsized, never wraps (title carries full
   value). Hidden on mobile (the labeled line takes over). `position: relative`
   anchors the absolutely-positioned copy button so it can never widen the grid
   track. `overflow: hidden` clips the (offscreen-until-hover) button too. */
.pin-cell {
  position: relative;
  color: var(--lg-text-dim);
  font-size: var(--lg-fs-xs);
  min-width: 0;
  overflow: hidden;
}
.pin-cell-val {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Small copy button pinned to the cell's right edge; revealed on hover/focus.
   Absolute so it never contributes to layout width (no horizontal overflow). */
.cell-copy {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: var(--lg-radius-sm);
  background: var(--lg-bg-raised);
  color: var(--lg-text-faint);
  cursor: pointer;
  opacity: 0;
}
.pin-cell:hover .cell-copy,
.cell-copy:focus-visible {
  opacity: 1;
}
.cell-copy:hover {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.cell-copy.copied {
  opacity: 1;
  color: var(--lg-ok);
}

.col-message {
  color: var(--lg-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.col-message.err {
  color: var(--lg-text-stderr);
}

/* Labeled pin line (fallback / mobile) — a compact `key=value` list that wraps
   under the message. Spans the whole grid width so it drops to its own row and
   NEVER introduces horizontal overflow. */
.pin-line {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 0 var(--lg-space-3);
  margin-top: var(--lg-space-1);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
  min-width: 0;
}
.pin-pair {
  white-space: normal;
  word-break: break-word;
}
.pin-k {
  color: var(--lg-text-faint);
}
.pin-v {
  color: var(--lg-text-dim);
}
/* Desktop, column mode: the labeled line is redundant with the columns. */
@media (min-width: 769px) {
  .log-row.has-columns .pin-line {
    display: none;
  }
}

.detail {
  padding: var(--lg-space-2) var(--lg-space-3) var(--lg-space-4);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
}
.detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lg-space-2) var(--lg-space-4);
  margin-bottom: var(--lg-space-2);
  color: var(--lg-text-dim);
}
.meta-k {
  color: var(--lg-text-faint);
  margin-right: var(--lg-space-2);
  text-transform: uppercase;
  font-size: var(--lg-fs-2xs);
  letter-spacing: 0.04em;
}
.detail-message {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--lg-text);
  background: var(--lg-bg);
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
  padding: var(--lg-space-2) var(--lg-space-3);
}
.detail-attrs {
  margin-top: var(--lg-space-3);
}
.detail-attrs-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lg-space-2);
  margin-bottom: var(--lg-space-1);
}
.detail-label {
  color: var(--lg-text-faint);
  text-transform: uppercase;
  font-size: var(--lg-fs-2xs);
  letter-spacing: 0.04em;
}
.save-default-btn {
  border: 1px solid var(--lg-border-strong);
  background: var(--lg-bg-surface);
  color: var(--lg-text-dim);
  font: inherit;
  font-size: var(--lg-fs-2xs);
  padding: 2px var(--lg-space-2);
  border-radius: var(--lg-radius-sm);
  cursor: pointer;
}
.save-default-btn:hover {
  color: var(--lg-text);
  border-color: var(--lg-accent);
}
.attrs-list {
  margin: 0;
  padding: var(--lg-space-2) var(--lg-space-3);
  list-style: none;
  background: var(--lg-bg);
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
}
.attr-entry {
  display: flex;
  align-items: baseline;
  gap: var(--lg-space-1);
  padding: 2px 0;
}
.attr-key,
.attr-val {
  border: none;
  background: transparent;
  font: inherit;
  padding: 0;
  cursor: pointer;
  word-break: break-word;
  text-align: left;
}
.attr-key {
  color: var(--lg-accent-soft);
}
.attr-val {
  color: var(--lg-text);
  flex: 1;
  min-width: 0;
}
.attr-key:hover,
.attr-val:hover {
  text-decoration: underline;
}
.attr-eq {
  color: var(--lg-text-faint);
}
.copy-btn,
.pin-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-faint);
  cursor: pointer;
}
.copy-btn {
  margin-left: auto;
}
.pin-btn {
  margin-left: 0;
}
.copy-btn:hover,
.pin-btn:hover {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.copy-btn.copied {
  color: var(--lg-ok);
}
.pin-btn.pinned {
  color: var(--lg-accent);
}

/* Mobile: two-line stack — line 1 (time · level · container), line 2 message,
   then the labeled pin line. Pinned COLUMNS never render here. */
@media (max-width: 768px) {
  .summary {
    display: flex;
    flex-direction: column;
    gap: var(--lg-space-1);
    min-height: var(--lg-touch);
    justify-content: center;
    padding: var(--lg-space-2) var(--lg-space-3);
  }
  .line1 {
    display: flex;
    align-items: center;
    gap: var(--lg-space-2);
    width: 100%;
  }
  .col-container {
    flex: 1;
    min-width: 0;
  }
  .col-message {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
    color: var(--lg-text-dim);
  }
  .col-message.err {
    color: var(--lg-text-stderr);
  }
  /* Columns collapse to nothing on mobile; the labeled line carries the pins. */
  .pin-cell {
    display: none;
  }
  .pin-line {
    margin-top: 0;
  }
}
</style>
